import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ArrowUp, ArrowDown, Trash2, Plus, CheckCircle2, Circle, Sparkles, Clapperboard } from 'lucide-react';
import { fmtTime as fmt } from './EditorTimelineRuler';

/**
 * Clip management panel for the active output type (LF or SF): create,
 * rename, select, remove and reorder. Each output type keeps its own
 * independent list of clips.
 */
export function EditorClipList({ clips, selectedClipId, onSelect, onAdd, onRename, onRemove, onReorder }) {
  return (
    <div className="space-y-3" data-testid="editor-clip-list">
      <Button onClick={onAdd} size="sm" className="w-full bg-[#3A7BFF] hover:bg-[#3A7BFF]/90" data-testid="add-clip-button">
        <Plus className="h-4 w-4 mr-2" />Add Clip at Playhead
      </Button>

      {clips.length === 0 && (
        <p className="text-sm text-[#8A93A6] text-center py-6" data-testid="editor-clip-list-empty">
          No clips yet for this output. Click "Add Clip" to create one.
        </p>
      )}

      <div className="space-y-2">
        {clips.map((clip, idx) => (
          <div
            key={clip.id}
            onClick={() => onSelect(clip.id)}
            className={`p-3 rounded-lg border cursor-pointer transition-all ${
              clip.id === selectedClipId ? 'border-[#3A7BFF] bg-[#3A7BFF]/10' : 'border-white/10 hover:bg-white/5'
            }`}
            data-testid={`clip-list-item-${clip.id}`}
          >
            <div className="flex items-center gap-2">
              {clip.approved ? (
                <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0" data-testid={`clip-approved-icon-${clip.id}`} />
              ) : (
                <Circle className="h-4 w-4 text-[#555E6E] shrink-0" />
              )}
              <Input
                value={clip.name}
                onChange={(e) => onRename(clip.id, e.target.value)}
                className="h-7 bg-transparent border-none text-white px-1 text-sm focus-visible:ring-1"
                data-testid={`clip-name-input-${clip.id}`}
              />
            </div>
            <div className="flex items-center justify-between mt-1 pl-6">
              <span className="text-xs text-[#8A93A6]">{fmt(clip.in_point)} – {fmt(clip.out_point)}</span>
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => onReorder(clip.id, -1)} disabled={idx === 0} className="disabled:opacity-20 text-[#8A93A6] hover:text-white" data-testid={`clip-move-up-${clip.id}`}>
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => onReorder(clip.id, 1)} disabled={idx === clips.length - 1} className="disabled:opacity-20 text-[#8A93A6] hover:text-white" data-testid={`clip-move-down-${clip.id}`}>
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => onRemove(clip.id)} className="text-red-400 hover:text-red-300 ml-1" data-testid={`clip-remove-${clip.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            {clip.master_composition_slice && (
              <div className="flex items-center gap-1.5 mt-1.5 pl-6" data-testid={`clip-master-origin-${clip.id}`}>
                <Badge className="bg-[#3A7BFF]/10 text-[#3A7BFF] border-0 text-[9px] px-1.5 flex items-center gap-1">
                  <Clapperboard className="h-2.5 w-2.5" />Master v{clip.source_master_version}
                </Badge>
                {clip.generated_from === 'ai_suggestion' && (
                  <Badge className="bg-amber-500/10 text-amber-300 border-0 text-[9px] px-1.5 flex items-center gap-1">
                    <Sparkles className="h-2.5 w-2.5" />AI
                  </Badge>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
